import { z } from 'zod';
import { workflowStageSchema } from '../constants/stages.js';
import { dateRangeQuerySchema } from '../reports/date-ranges.js';
import { pnlBasisSchema } from './expenses.js';

/**
 * The shapes the dashboard and the five reports travel in.
 *
 * Everything here is a READ. Task 26 presents what other tasks recorded and introduces no
 * domain data of its own, so there is not a single write schema in this file — which is also
 * why every figure below carries the counts it was summed from. A revenue total beside the
 * number of documents that produced it is a figure an owner can reconcile against the ledger
 * screen; a bare total is one they have to trust.
 *
 * Written schema-first, with the TypeScript types inferred from the schemas rather than
 * declared beside them. These payloads are wide and nested, and a hand-written interface next
 * to a hand-written validator is two descriptions of one thing that drift the first time a
 * field is added to only one of them.
 *
 * All amounts are integer paise, summed by the database and never averaged into a rupee.
 */

// --- Query contracts ---------------------------------------------------------------------

export const reportQuerySchema = dateRangeQuerySchema;
export type ReportQuery = z.infer<typeof reportQuerySchema>;

/** The report list endpoints take a range plus how many rows the leaderboards return. */
export const rankedReportQuerySchema = z.intersection(
  reportQuerySchema,
  z.object({ limit: z.coerce.number().int().min(1).max(100).default(10) }),
);
export type RankedReportQuery = z.infer<typeof rankedReportQuerySchema>;

/**
 * The customers report additionally needs "not seen in how long?". Ninety days is the default
 * because a tailoring customer who has not been back in three months has usually gone
 * somewhere else, and that is the list worth ringing.
 */
export const customerReportQuerySchema = z.intersection(
  rankedReportQuerySchema,
  z.object({ lapsedDays: z.coerce.number().int().min(7).max(730).default(90) }),
);
export type CustomerReportQuery = z.infer<typeof customerReportQuerySchema>;

/**
 * The financial report reads revenue on the basis the owner picked, as the P&L does — and
 * against the SAME schema, so the two screens cannot drift into meaning different things by
 * the word "cash".
 */
export const financialReportQuerySchema = z.intersection(
  reportQuerySchema,
  z.object({ basis: pnlBasisSchema.default('cash') }),
);
export type FinancialReportQuery = z.infer<typeof financialReportQuerySchema>;

/** Every report echoes the range it was computed over, resolved from the preset. */
export const reportRangeSchema = z.object({
  preset: z.string(),
  fromDay: z.string(),
  toDay: z.string(),
  from: z.string(),
  to: z.string(),
  days: z.number().int(),
});
export type ReportRange = z.infer<typeof reportRangeSchema>;

// --- The dashboard -------------------------------------------------------------------------

export const dashboardCountsSchema = z.object({
  /** Live items promised for today. */
  dueToday: z.number().int(),
  /** Live items whose promised date has passed that are not yet ready or delivered. */
  overdue: z.number().int(),
  /** Finished and waiting for the customer to walk in. */
  readyForCollection: z.number().int(),
  /** Everything between booked and ready — the shop's current workload. */
  inProgress: z.number().int(),
  ordersBookedToday: z.number().int(),
  /** Assignments a karigar has raised a problem on and nobody has resolved. */
  flagged: z.number().int(),
});
export type DashboardCounts = z.infer<typeof dashboardCountsSchema>;

/**
 * The money half of the dashboard, `null` for a caller the matrix does not let near rupees.
 * Null rather than zeroed: a tile reading "₹0 outstanding" to somebody who is simply not
 * allowed to know is worse than no tile.
 */
export const dashboardMoneySchema = z.object({
  outstandingPaise: z.number().int(),
  outstandingOrderCount: z.number().int(),
  collectedTodayPaise: z.number().int(),
  collectedTodayCount: z.number().int(),
  collectedYesterdayPaise: z.number().int(),
  bookedTodayPaise: z.number().int(),
  /** Piece-rate earnings accrued and not yet settled — what the shop owes its karigars. */
  unsettledWagesPaise: z.number().int(),
});
export type DashboardMoney = z.infer<typeof dashboardMoneySchema>;

export const dashboardWorkloadRowSchema = z.object({
  stage: workflowStageSchema,
  items: z.number().int(),
  /** Days the longest-waiting garment has been sitting in this stage. */
  oldestDays: z.number().int(),
});
export type DashboardWorkloadRow = z.infer<typeof dashboardWorkloadRowSchema>;

export const dashboardStaffRowSchema = z.object({
  staffUserId: z.string(),
  staffName: z.string(),
  activeItems: z.number().int(),
  dueToday: z.number().int(),
});
export type DashboardStaffRow = z.infer<typeof dashboardStaffRowSchema>;

export const dashboardAttentionRowSchema = z.object({
  itemId: z.string(),
  orderId: z.string(),
  orderNumber: z.string(),
  customerName: z.string(),
  garmentTypeLabel: z.string(),
  stage: workflowStageSchema,
  /** Why it is on this list: overdue, or a karigar flagged a problem. */
  reason: z.enum(['overdue', 'flagged']),
  detail: z.string().nullable(),
  daysLate: z.number().int(),
});
export type DashboardAttentionRow = z.infer<typeof dashboardAttentionRowSchema>;

export const dashboardSchema = z.object({
  /** The shop day these figures describe. */
  day: z.string(),
  computedAt: z.string(),
  /** True when the figures came from the cached snapshot rather than a fresh pass. */
  cached: z.boolean(),
  counts: dashboardCountsSchema,
  money: dashboardMoneySchema.nullable(),
  workload: z.array(dashboardWorkloadRowSchema),
  staff: z.array(dashboardStaffRowSchema),
  attention: z.array(dashboardAttentionRowSchema),
});
export type Dashboard = z.infer<typeof dashboardSchema>;

/**
 * Every tile, and the list it opens.
 *
 * The route lives HERE, beside the figure, because "a number without a route to the underlying
 * records is decoration" is a rule that only holds if adding a tile forces you to say where it
 * goes. A tile with no destination cannot be declared.
 */
export interface DashboardTileDefinition {
  readonly key: string;
  /** Whether it belongs to the money block, and so disappears for a caller without it. */
  readonly money: boolean;
  /** The filtered list behind the number. */
  readonly to: string;
}

export const DASHBOARD_TILES: readonly DashboardTileDefinition[] = Object.freeze([
  { key: 'dueToday', money: false, to: '/orders?due=today' },
  { key: 'overdue', money: false, to: '/orders?due=overdue' },
  { key: 'ready', money: false, to: '/orders?stage=ready' },
  { key: 'inProgress', money: false, to: '/board' },
  { key: 'outstanding', money: true, to: '/payments?filter=outstanding' },
  { key: 'collectedToday', money: true, to: '/payments?range=today' },
  { key: 'bookedToday', money: false, to: '/orders?booked=today' },
  { key: 'unsettledWages', money: true, to: '/payouts' },
]);

// --- Sales ------------------------------------------------------------------------------------

export const salesTotalsSchema = z.object({
  /** What the garments came to before any discount — the sum of every line. */
  subtotalPaise: z.number().int(),
  discountPaise: z.number().int(),
  /** What the shop actually billed: subtotal less discount. */
  revenuePaise: z.number().int(),
  orderCount: z.number().int(),
  itemCount: z.number().int(),
  /** Revenue divided by orders, in paise, rounded once at the end. */
  averageOrderPaise: z.number().int(),
});
export type SalesTotals = z.infer<typeof salesTotalsSchema>;

export const salesByGarmentRowSchema = z.object({
  garmentTypeKey: z.string(),
  garmentTypeLabel: z.string(),
  quantity: z.number().int(),
  revenuePaise: z.number().int(),
  share: z.number(),
});
export type SalesByGarmentRow = z.infer<typeof salesByGarmentRowSchema>;

export const salesByCustomerRowSchema = z.object({
  customerId: z.string(),
  customerName: z.string(),
  customerPhone: z.string(),
  orderCount: z.number().int(),
  revenuePaise: z.number().int(),
});
export type SalesByCustomerRow = z.infer<typeof salesByCustomerRowSchema>;

export const salesByDayRowSchema = z.object({
  day: z.string(),
  revenuePaise: z.number().int(),
  orderCount: z.number().int(),
});
export type SalesByDayRow = z.infer<typeof salesByDayRowSchema>;

/**
 * Alterations booked in the window, beside the sales rather than in them. An alteration is not a
 * sale of new work — folding its charge into revenue would inflate stitching revenue and drag the
 * average order down — so it gets its own line. `collectedPaise` is what has been paid on them so
 * far; the money itself is already in the financial report's collections, like any payment.
 */
export const salesAlterationsSchema = z.object({
  count: z.number().int(),
  /** How many of them carried a charge. Most re-fits are free. */
  chargedCount: z.number().int(),
  chargePaise: z.number().int(),
  collectedPaise: z.number().int(),
});
export type SalesAlterations = z.infer<typeof salesAlterationsSchema>;

export const salesReportSchema = z.object({
  range: reportRangeSchema,
  previousRange: reportRangeSchema,
  totals: salesTotalsSchema,
  previousTotals: salesTotalsSchema,
  change: z.object({
    revenuePaise: z.number().int(),
    orderCount: z.number().int(),
    averageOrderPaise: z.number().int(),
  }),
  byGarmentType: z.array(salesByGarmentRowSchema),
  byCustomer: z.array(salesByCustomerRowSchema),
  byDay: z.array(salesByDayRowSchema),
  /** Absent when the window booked no alterations, like the counter-sales row. */
  alterations: salesAlterationsSchema.optional(),
});
export type SalesReport = z.infer<typeof salesReportSchema>;

// --- Production --------------------------------------------------------------------------------

export const stageDurationRowSchema = z.object({
  stage: workflowStageSchema,
  /** Moves OUT of this stage that were recorded in the range. */
  moves: z.number().int(),
  averageMs: z.number().int(),
  medianMs: z.number().int(),
  longestMs: z.number().int(),
});
export type StageDurationRow = z.infer<typeof stageDurationRowSchema>;

export const workInProgressRowSchema = z.object({
  stage: workflowStageSchema,
  items: z.number().int(),
  oldestDays: z.number().int(),
  /** Live items in this stage already past their promised date. */
  overdue: z.number().int(),
});
export type WorkInProgressRow = z.infer<typeof workInProgressRowSchema>;

export const productionByDayRowSchema = z.object({
  day: z.string(),
  booked: z.number().int(),
  delivered: z.number().int(),
});
export type ProductionByDayRow = z.infer<typeof productionByDayRowSchema>;

export const productionReportSchema = z.object({
  range: reportRangeSchema,
  throughput: z.object({
    itemsBooked: z.number().int(),
    itemsDelivered: z.number().int(),
    ordersDelivered: z.number().int(),
    /** Delivered items divided by days in the range, to one decimal. */
    itemsPerDay: z.number(),
  }),
  delivery: z.object({
    delivered: z.number().int(),
    onTime: z.number().int(),
    late: z.number().int(),
    /** `onTime / (onTime + late)`, or 0 when nothing was judged. Display only. */
    onTimeRate: z.number(),
    averageLateDays: z.number(),
    /** Delivered items that never carried a promised date — neither on time nor late. */
    unpromised: z.number().int(),
  }),
  stageDurations: z.array(stageDurationRowSchema),
  workInProgress: z.array(workInProgressRowSchema),
  byDay: z.array(productionByDayRowSchema),
});
export type ProductionReport = z.infer<typeof productionReportSchema>;

// --- Staff productivity ------------------------------------------------------------------------

export const staffProductivityRowSchema = z.object({
  staffUserId: z.string(),
  staffName: z.string(),
  /** Assignments completed in the range. */
  jobsCompleted: z.number().int(),
  /** Garments, which is not the same number — one assignment can cover a quantity of three. */
  quantity: z.number().int(),
  earnedPaise: z.number().int(),
  settledPaise: z.number().int(),
  unsettledPaise: z.number().int(),
  /** Started to completed, averaged. Null when nothing recorded a start. */
  averageTurnaroundMs: z.number().int().nullable(),
  reworks: z.number().int(),
});
export type StaffProductivityRow = z.infer<typeof staffProductivityRowSchema>;

export const staffProductivityReportSchema = z.object({
  range: reportRangeSchema,
  rows: z.array(staffProductivityRowSchema),
  totals: z.object({
    jobsCompleted: z.number().int(),
    quantity: z.number().int(),
    earnedPaise: z.number().int(),
    settledPaise: z.number().int(),
    unsettledPaise: z.number().int(),
  }),
  /** True when the caller is a karigar seeing only themselves. */
  ownScope: z.boolean(),
});
export type StaffProductivityReport = z.infer<typeof staffProductivityReportSchema>;

// --- Customers ---------------------------------------------------------------------------------

export const topCustomerRowSchema = z.object({
  customerId: z.string(),
  customerName: z.string(),
  customerPhone: z.string(),
  orderCount: z.number().int(),
  revenuePaise: z.number().int(),
  lastOrderAt: z.string(),
});
export type TopCustomerRow = z.infer<typeof topCustomerRowSchema>;

export const lapsedCustomerRowSchema = z.object({
  customerId: z.string(),
  customerName: z.string(),
  customerPhone: z.string(),
  lastOrderAt: z.string().nullable(),
  daysSinceLastOrder: z.number().int().nullable(),
  lifetimePaise: z.number().int(),
  orderCount: z.number().int(),
});
export type LapsedCustomerRow = z.infer<typeof lapsedCustomerRowSchema>;

export const customerReportSchema = z.object({
  range: reportRangeSchema,
  lapsedDays: z.number().int(),
  totals: z.object({
    /** Customers whose FIRST EVER order falls in the range. */
    newCustomers: z.number().int(),
    /** Customers who ordered in the range and had ordered before it. */
    returningCustomers: z.number().int(),
    activeCustomers: z.number().int(),
    revenuePaise: z.number().int(),
    newCustomerRevenuePaise: z.number().int(),
    returningCustomerRevenuePaise: z.number().int(),
  }),
  top: z.array(topCustomerRowSchema),
  lapsed: z.array(lapsedCustomerRowSchema),
  byDay: z.array(
    z.object({
      day: z.string(),
      newCustomers: z.number().int(),
      returningCustomers: z.number().int(),
    }),
  ),
});
export type CustomerReport = z.infer<typeof customerReportSchema>;

// --- Financial ------------------------------------------------------------------------------------

export const receivablesBucketSchema = z.object({
  /** `0-30`, `31-60`, `61-90`, `90+` — days since the order was booked. */
  key: z.string(),
  orderCount: z.number().int(),
  amountPaise: z.number().int(),
});
export type ReceivablesBucket = z.infer<typeof receivablesBucketSchema>;

export const financialMonthRowSchema = z.object({
  month: z.string(),
  revenuePaise: z.number().int(),
  expensePaise: z.number().int(),
  profitPaise: z.number().int(),
});
export type FinancialMonthRow = z.infer<typeof financialMonthRowSchema>;

export const financialReportSchema = z.object({
  range: reportRangeSchema,
  basis: pnlBasisSchema,
  totals: z.object({
    revenuePaise: z.number().int(),
    expensePaise: z.number().int(),
    profitPaise: z.number().int(),
    revenueEntryCount: z.number().int(),
    expenseEntryCount: z.number().int(),
  }),
  byCategory: z.array(
    z.object({
      categoryId: z.string(),
      categoryKey: z.string(),
      categoryLabel: z.string(),
      amountPaise: z.number().int(),
      count: z.number().int(),
    }),
  ),
  receivables: z.object({
    totalPaise: z.number().int(),
    orderCount: z.number().int(),
    buckets: z.array(receivablesBucketSchema),
  }),
  wageLiability: z.object({
    totalPaise: z.number().int(),
    staffCount: z.number().int(),
  }),
  byMonth: z.array(financialMonthRowSchema),
});
export type FinancialReport = z.infer<typeof financialReportSchema>;

/** The five reports, in the order the screen lists them. */
export const REPORT_KEYS = ['sales', 'production', 'staff', 'customers', 'financial'] as const;
export type ReportKey = (typeof REPORT_KEYS)[number];
export const reportKeySchema = z.enum(REPORT_KEYS);
